import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { toast } from 'react-toastify';
import Select from 'react-select';
import './VehicleDetails.css';
import MasterService from '../../services/MasterService';

const toCamelCase = (str) => {
    if (!str) return '';
    return str.toLowerCase()
             .replace(/_/g, ' ')
             .replace(/\b\w/g, char => char.toUpperCase());
}

const VehicleDetails = () => {
    const { leadId } = useParams();
    const navigate = useNavigate();
    const location = useLocation();
    const [vehicleDetails, setVehicleDetails] = useState(null);
    const [loading, setLoading] = useState(false);
    const [auctionStatus, setAuctionStatus] = useState('');
    const [remarks, setRemarks] = useState('');
    const [updating, setUpdating] = useState(false);

    const auctionStatusOptions = [
        { value: 'BID_RECEIVED', label: 'Bid Received' },
        { value: 'SOLD', label: 'Sold' },
        { value: 'UNSOLD', label: 'Unsold' },
        { value: 'REJECTED', label: 'Rejected' }
    ];

    useEffect(() => {
        fetchVehicleDetails();
    }, [leadId]);

    const fetchVehicleDetails = async () => {
        setLoading(true);
        try {
            // Get dealer_id from location state passed from inventory list
            const dealerId = location.state?.dealerId; // Fallback to 1 if no dealer_id found
            
            const result = await MasterService.getVehicleDetails(parseInt(leadId), dealerId);
            
            if (result.status === 200) {
                setVehicleDetails(result.data.data);
                setAuctionStatus(result.data?.data?.basic_details?.auction_status || '');
                setRemarks(result.data?.data?.basic_details?.remarks || '');
            } else {
                toast.error('Failed to fetch vehicle details');
            }
        } catch (error) {
            console.error('Error fetching vehicle details:', error);
            toast.error('Failed to fetch vehicle details');
        } finally {
            setLoading(false);
        }
    };

    const handleStatusUpdate = async () => {
        setUpdating(true);
        try {
            const result = await MasterService.updateAuctionStatus(parseInt(leadId), auctionStatus, remarks);
            
            if (result.status === 200) {
                toast.success('Auction status updated successfully');
                // Refresh the vehicle details to get updated data
                fetchVehicleDetails();
            } else {
                toast.error('Failed to update auction status');
            }
        } catch (error) {
            console.error('Error updating auction status:', error);
            toast.error('Failed to update auction status');
        } finally {
            setUpdating(false);
        }
    };

    const handleBack = () => {
        navigate(-1); // Go back to previous page
    };

    const formatPrice = (price) => {
        if (!price) return '-';
        return new Intl.NumberFormat('en-IN', {
            maximumFractionDigits: 0
        }).format(price);
    };

    const formatDate = (dateString) => {
        if (!dateString) return '-';
        const date = new Date(dateString);
        return date.toLocaleString('en-US', {
            year: 'numeric',
            month: '2-digit',
            day: '2-digit',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    if (loading) {
        return (
            <div className="vehicle-details-container">
                <div className="loading-container">
                    <div className="loading-spinner"></div>
                    <p>Loading vehicle details...</p>
                </div>
            </div>
        );
    }

    if (!vehicleDetails) {
        return (
            <div className="vehicle-details-container">
                <div className="error-container">
                    <p>Vehicle details not found</p>
                     <div className="arrow-bck" onClick={handleBack}>
                   <i className='ic-arrow_back'></i>
                 </div>
                </div>
            </div>
        );
    }

    console.log("*************",vehicleDetails)

    return (
        <div className="vehicle-details-container">
            {/* Header with Vehicle Info */}
            <header className="vehicle-details-header">
                <div className="header-left">
                     <div className="arrow-bck" onClick={handleBack}>
                   <i className='ic-arrow_back'></i>
                 </div>
                    <div className="vehicle-identity">
                        <div className="vehicle-info">
                            <div className="mmvy-info vechile-pict-txt">
                                <span className="mmvy-text">
                                    {vehicleDetails.make_name} {vehicleDetails.model_name} {vehicleDetails.variant_name} {vehicleDetails.basic_details?.make_year} ({vehicleDetails.basic_details?.plate_number})
                                </span>
                                  <span className="lead-id">Lead ID: {leadId}</span>
                            </div>
                          
                        </div>
                    </div>
                </div>
            </header>

            <div className="vehicle-details-content vechile-photo-detail-top">

                {/* Vehicle Pictures Section */}
                <section className="vehicle-pictures-section">
                    <h2>Vehicle Pictures</h2>
                    <div className="pictures-grid">
                        {vehicleDetails?.vehicle_images && vehicleDetails?.vehicle_images.length > 0 ? (
                            vehicleDetails?.vehicle_images.map((image, index) => (
                                <div key={index} className="picture-item">
                                    <img 
                                        src={image.doc_url} 
                                        alt={`Vehicle image ${index + 1}`}
                                        onError={(e) => {
                                            e.target.src = '/placeholder-image.png';
                                        }}
                                    />
                                </div>
                            ))
                        ) : (
                            <p className="no-pictures">No pictures available</p>
                        )}
                    </div>
                </section>

                {/* Auction Details Section */}
                <section className="auction-details-section">
                    <h2>Auction Details</h2>
                    <div className="info-grid">
                        <div className="info-item">
                            <label>Deal Price:</label>
                            <span>{formatPrice(location.state?.dealPrice)}</span>
                        </div>
                        <div className="info-item">
                            <label>OTO Base Price:</label>
                            <span>{formatPrice(location.state?.otoBasePrice)}</span>
                        </div>
                        <div className="info-item">
                            <label>Winning Price:</label>
                            <span>{formatPrice(location.state?.winningPrice)}</span>
                        </div>
                        <div className="info-item">
                            <label>Bid Count:</label>
                            <span>{location.state.bidCount || '-'}</span>
                        </div>
                        <div className="info-item">
                            <label>Bidder Count:</label>
                            <span>{location.state.bidderCount || '-'}</span>
                        </div>             
                        <div className="info-item">
                            <label>Visit Count:</label>
                            <span>{location.state.visitCount || '-'}</span>
                        </div>
                    </div>
                </section>

                {/* Auction Status and Remarks Section */}
                <section className="status-remarks-section">
                    <h2>Auction Status & Remarks</h2>
                    <div className="status-remarks-form">
                        <div className="form-group">
                            <label htmlFor="auction-status">Auction Status:</label>
                            <Select
                                id="auction-status"
                                value={auctionStatus ? { value: auctionStatus, label: toCamelCase(auctionStatus) } : null}
                                onChange={(selectedOption) => setAuctionStatus(selectedOption ? selectedOption.value : '')}
                                options={[
                                    { value: '', label: 'Select Status' },
                                    ...auctionStatusOptions.map(option => ({ value: option.value, label: option.label }))
                                ]}
                                placeholder="Select Status"
                                isClearable
                                isSearchable
                            />
                        </div>
                        <div className="form-group">
                            <label htmlFor="remarks">Remarks:</label>
                            <textarea
                                id="remarks"
                                className="remarks-textarea"
                                value={remarks}
                                onChange={(e) => setRemarks(e.target.value)}
                                rows="4"
                                placeholder="Enter remarks..."
                            />
                        </div>
                        <div className="form-actions">
                            <button
                                className="btn-primary"
                                onClick={handleStatusUpdate}
                                disabled={updating || !auctionStatus}
                            >
                                {updating ? 'Updating...' : 'Update Status'}
                            </button>
                        </div>
                    </div>
                </section>
            </div>
        </div>
    );
};

export default VehicleDetails;
